import * as THREE from 'three';
import { foreachMesh } from '../helpers/ObjectHelpers';

export const InteractionMode = {
  standardMode: 1,
  editMode: 2,
  imageMode: 3,
};

export const DragMode = {
  rotate: 1,
  pan: 2,
};

export class InteractionHandler {
  constructor(imageEngine) {
    this.imageEngine = imageEngine;
    this._highlightedObjects = [];
    this._selectedObjects = [];
    this._hoveredObjects = [];
    this.interactionMode = InteractionMode.standardMode;
    this.dragMode = DragMode.rotate;
    this.enabledRotate = undefined;
    this.enabledZoom = undefined;
  }

  reset() {
    this.clearSelectedObjects();
    this.unhighlightAllObjects();
  }

  onObjectClick(object) {
    if (object.onClick) {
      if (object.onClick() === false) {
        return;
      }
    }
    if (object.selectable) {
      this.selectObject(object);
    }
  }

  selectObject(object) {
    this.clearSelectedObjects();

    // TODO: Is this relevant?
    //this.imageEngine.scene.updateMatrixWorld();

    if (object.onSelect) {
      object.onSelect();
    }

    this._selectedObjects.push(object);
    if (object.data && object.data.connectedObjects) {
      for (var i = 0; i < object.data.connectedObjects.length; i++) {
        var obj2 = object.data.connectedObjects[i];
        this._selectedObjects.push(obj2);
        if (obj2.onSelect) {
          obj2.onSelect();
        }
      }
    }
  }

  clearSelectedObjects() {
    for (var i = 0; i < this._selectedObjects.length; i++) {
      var object = this._selectedObjects[i];
      if (object.onDeselect) {
        object.onDeselect();
      }
    }
    this._selectedObjects = [];

    this.imageEngine.inputHandler.closeInputs();
    this.imageEngine.requireRenderUpdate = true;
  }

  highlightObject(object) {
    this._highlightedObjects.push(object);
    if (object.onHighlight) {
      object.onHighlight();
    }
  }

  unhighlightAllObjects() {
    for (const object of this._highlightedObjects) {
      if (object.onUnhighlight) {
        object.onUnhighlight();
      }
    }
    this._highlightedObjects = [];
  }

  getSelectedIds() {
    const selectedIds = [];
    for (const obj of this._selectedObjects) {
      selectedIds.push(obj.data.identifier);
    }
    return selectedIds;
  }

  selectObjectsByIds(ids) {
    const facade = this.imageEngine.facade;
    const windows = facade.windows;

    for (var winix = 0; winix < windows.length; winix++) {
      for (const object of windows[winix].interactableObjects) {
        if (ids.indexOf(object.data.identifier) !== -1 && object.selectable) {
          this._selectedObjects.push(object);
          if (object.onSelect) {
            object.onSelect();
          }
        }
      }
    }
  }

  _createRaycaster() {
    const cw = this.imageEngine.renderer.domElement.offsetWidth;
    const ch = this.imageEngine.renderer.domElement.offsetHeight;
    const mouse = this.imageEngine.mouseHandler.mouse;
    var coords = {
      x: (mouse.x / cw) * 2 - 1,
      y: -((mouse.y / ch) * 2 - 1),
    };
    var raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(coords, this.imageEngine.camera.obj);
    return raycaster;
  }

  rayTest(clicked) {
    if (this.interactionMode !== InteractionMode.editMode) return;
    const mouse = this.imageEngine.mouseHandler.mouse;
    if (mouse === undefined) return;

    var oldHoveredObjects = this._hoveredObjects;
    var newHoveredObjects = [];

    var raycaster = this._createRaycaster();

    var testObjects = [];
    for (const window of this.imageEngine.facade.windows) {
      testObjects = testObjects.concat(window.interactableObjects);
    }
    var intersects = raycaster
      .intersectObjects(testObjects, true)
      .sort(function (a, b) {
        if (a.object.renderOrder !== b.object.renderOrder) {
          return b.object.renderOrder - a.object.renderOrder;
        }
        return a.distance - b.distance;
      });
    var intersection = intersects.find(x => x.object instanceof THREE.Mesh);
    var intersectionObject = intersection ? intersection.object : undefined;
    while (
      intersectionObject &&
      !testObjects.find(x => x === intersectionObject)
    ) {
      intersectionObject = intersectionObject.parent;
    }

    if (clicked) {
      if (intersectionObject) {
        this.imageEngine.interactionHandler.onObjectClick(intersectionObject);
      } else {
        this.imageEngine.interactionHandler.clearSelectedObjects();
      }
    } else {
      if (
        intersectionObject &&
        this.imageEngine.mouseHandler.isHoveringCanvas
      ) {
        newHoveredObjects.push(intersectionObject);
        if (
          intersectionObject.data &&
          intersectionObject.data.connectedObjects
        ) {
          for (
            var i = 0;
            i < intersectionObject.data.connectedObjects.length;
            i++
          ) {
            newHoveredObjects.push(intersectionObject.data.connectedObjects[i]);
          }
        }
      }
    }

    for (var i = 0; i < oldHoveredObjects.length; i++) {
      var obj = oldHoveredObjects[i];
      if (!newHoveredObjects.includes(obj)) {
        if (obj.onUnhover) {
          obj.onUnhover();
        }
      }
    }

    this._hoveredObjects = newHoveredObjects;

    if (!this.imageEngine.mouseHandler.isUsingTouch) {
      for (var i = 0; i < newHoveredObjects.length; i++) {
        var obj = newHoveredObjects[i];
        if (oldHoveredObjects.includes(obj)) {
          if (obj.onHover) {
            obj.onHover();
          }
        }
      }
    }
  }

  rayTestMullionName() {
    var raycaster = this._createRaycaster();
    var allProfiles = [];
    for (var i = 0; i < this.imageEngine.facade.windows.length; i++) {
      allProfiles = allProfiles.concat(
        this.imageEngine.facade.windows[i].movableProfiles
      );
    }
    var intersects = raycaster.intersectObjects(allProfiles, false);
    if (intersects.length > 0) {
      var mul = intersects[0].object;
      console.log('DEBUG: Clicked on mullion "' + mul.data.symbolName + '"');
    }
  }

  updateAllMeasuredWindows() {
    this.imageEngine.facade.toggleAllMeasurements();
  }

  updateMeasuredWindow() {
    var raycaster = this._createRaycaster();
    var allWindows = [];
    for (var i = 0; i < this.imageEngine.facade.windows.length; i++) {
      foreachMesh(this.imageEngine.facade.windows[i].obj, mesh => {
        allWindows.push(mesh);
      });
    }
    var intersects = raycaster.intersectObjects(allWindows, false);
    var found = undefined;
    for (var i = 0; i < intersects.length && !found; i++) {
      if (intersects.length > 0) {
        var win = intersects[i].object;
        while (win && win.name !== 'Window') {
          win = win.parent;
        }
        if (win) {
          found = win.data.window;
        }
      }
    }
    if (found) {
      this.imageEngine.facade.toggleMeasurement(found);
    }
  }

  hasSelectedObjects() {
    return this._selectedObjects.length > 0;
  }

  _moveSelectedObjects(setValue, allowHorizontal, allowVertical) {
    if (allowHorizontal === undefined) allowHorizontal = true;
    if (allowVertical === undefined) allowVertical = true;

    for (var i = 0; i < this._selectedObjects.length; i++) {
      var obj = this._selectedObjects[i];
      if (obj.data.axis === 'y') {
        if (allowVertical) {
          setValue(obj);
        }
      } else {
        if (allowHorizontal) {
          setValue(obj);
        }
      }
      obj.move(obj.data.currPos);
    }

    this.imageEngine.facade.rebuild();
  }

  moveSelectedObjectsRelatively(distance, allowHorizontal, allowVertical) {
    this._moveSelectedObjects(
      obj => {
        if (obj.data.axis === 'y') {
          obj.data.currPos -= distance;
        } else {
          obj.data.currPos += distance;
        }
      },
      allowHorizontal,
      allowVertical
    );
  }

  moveSelectedObjects(position, allowHorizontal, allowVertical) {
    this._moveSelectedObjects(
      obj => {
        obj.data.currPos = position;
      },
      allowHorizontal,
      allowVertical
    );
  }

  alignWithSelected(obj) {
    if (!this.hasSelectedObjects()) {
      return;
    }

    var newPos = this._selectedObjects[0].data.currPos;
    obj.move(newPos);
    for (let i = 0; i < obj.data.connectedObjects.length; i++) {
      obj.data.connectedObjects[i].move(newPos);
    }

    this.imageEngine.facade.rebuild();
  }
}
